import { graphPostJson } from "../../email/services/graph";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import { parseGraphUtc } from "../domain/time";
import type { Schedule } from "../domain/freeBusy";

/** Graph getSchedule accepts at most this many people per request. */
export const MAX_SCHEDULES = 20;

interface ScheduleResponse {
  readonly value?: readonly {
    readonly scheduleId?: string;
    readonly error?: unknown;
    readonly scheduleItems?: readonly {
      readonly status?: string;
      readonly start?: { readonly dateTime?: string } | null;
      readonly end?: { readonly dateTime?: string } | null;
    }[];
  }[];
}

/**
 * Colleagues' busy times between `start` and `end`. Anyone Graph couldn't read
 * (an error for that person, or beyond the 20-person limit) comes back as
 * "unknown" — never as free.
 */
export async function fetchSchedules(
  token: string,
  refresh: RefreshTokenFn | undefined,
  emails: readonly string[],
  start: Date,
  end: Date,
): Promise<Map<string, Schedule>> {
  const unique = [...new Set(emails.map((e) => e.toLowerCase()))];
  const result = new Map<string, Schedule>(unique.map((e) => [e, { status: "unknown", busy: [] }]));
  const asked = unique.slice(0, MAX_SCHEDULES);
  if (asked.length === 0) return result;

  const response = await graphPostJson<ScheduleResponse>(
    "/me/calendar/getSchedule",
    {
      schedules: asked,
      startTime: { dateTime: start.toISOString().slice(0, 19), timeZone: "UTC" },
      endTime: { dateTime: end.toISOString().slice(0, 19), timeZone: "UTC" },
      availabilityViewInterval: 15,
    },
    token,
    refresh,
  );

  for (const entry of response.value ?? []) {
    const email = (entry.scheduleId ?? "").toLowerCase();
    if (!result.has(email) || entry.error) continue;
    const busy = (entry.scheduleItems ?? [])
      .filter((item) => item.status !== "free")
      .map((item) => ({ start: parseGraphUtc(item.start?.dateTime), end: parseGraphUtc(item.end?.dateTime) }))
      .filter((b): b is { start: Date; end: Date } => b.start !== null && b.end !== null)
      .map((b) => ({ startMs: b.start.getTime(), endMs: b.end.getTime() }));
    result.set(email, { status: "ok", busy });
  }
  return result;
}
